import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { CheckBoxEventValue } from '@interstate/components/CheckBox';
import { CheckBox } from '@interstate/components/CheckBox';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import { Typography } from '@interstate/components/Typography';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { LEASE } from '@makemydeal/dr-platform-types';

import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';

import {
    PURCHASE,
    IS_BUYER_REGISTERED_OWNER,
    VEHICLE_IS_REGISTERED_IN_SAME_STATE,
    INCLUDE_TRADE_WITH_OFFER,
    IS_TRADE_IN_ACTIVE_LEASE
} from '../../constants';

import { TradeCheckboxContainer } from './trade.interstate.style';
import { useSingleTradeInFormProvider } from './MultiTradeIn/TradeInFormProvider';

export enum CheckboxType {
    LEASE = 'lease',
    BUYER_REGISTERED = 'buyerRegistered',
    VEHICLE_REGISTERED = 'vehicleRegistered',
    INCLUDE_OFFER = 'includeOffer'
}

interface TradeCheckboxesProps {
    type: CheckboxType;
    isEditDisabled?: boolean;
    tradeInputsDisabled?: boolean;
}

const CHECKBOX_IDS = {
    BUYER_REGISTERED_OWNER: 'is-buyer-registered-owner',
    VEHICLE_REGISTERED_SAME_STATE: 'vehicle-is-registered-in-same-state',
    LEASE: 'lease',
    INCLUDE_OFFER: 'includeOffer'
} as const;

export const TradeCheckboxes: React.FC<TradeCheckboxesProps> = ({ type, isEditDisabled = false, tradeInputsDisabled }) => {
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const { tradeInIndex } = useSingleTradeInFormProvider();
    const tradeInOwnershipType = useSelector(tradeInsSelectors.getTradeOwnershipType(tradeInIndex));

    const isBuyerRegisteredOwner = useSelector(tradeInsSelectors.getIsBuyerRegisteredOwner(tradeInIndex));
    const isVehicleRegisteredInSameState = useSelector(tradeInsSelectors.getIsSellingVehicleRegisteredInSameState(tradeInIndex));
    const shouldIncludeTradeInWithOffer = useSelector(tradeInsSelectors.getShouldIncludeTradeIn(tradeInIndex));
    const includeTradeCheckboxValue = shouldIncludeTradeInWithOffer ? 'includeTrade' : '';
    const isTradeInDeleted = useSelector(tradeInsSelectors.isTradeInDeleted(tradeInIndex));

    const handleIncludeTradeWithOfferChange: InterstateOnChangeCallback<CheckBoxEventValue> = (event) => {
        const isTradeInIncluded = Boolean(event.target.value.checked);
        dispatch(tradeInsActionCreators.includeTradeChanged(isTradeInIncluded, tradeInIndex));
        dispatch(interactionAnalyticsActionCreators.tradeIncludeWithOfferCheckbox());
    };

    const handleBuyerRegisteredOwnerChange: InterstateOnChangeCallback<CheckBoxEventValue> = (event) => {
        const checked = Boolean(event.target.value.checked);
        dispatch(tradeInsActionCreators.updateTradeInIsBuyerRegisteredOwner(checked, tradeInIndex));
    };

    const handleVehicleRegisteredSameStateChange: InterstateOnChangeCallback<CheckBoxEventValue> = (event) => {
        const isVehicleInSameState = Boolean(event.target.value.checked);
        dispatch(tradeInsActionCreators.updateTradeInIsSellingVehicleRegisteredInSameState(isVehicleInSameState, tradeInIndex));
    };
    const handleOwnershipTypeChange: InterstateOnChangeCallback<CheckBoxEventValue> = (event) => {
        const changeOwnershipType = event.target.value.checkboxValue === LEASE ? PURCHASE : LEASE;
        dispatch(tradeInsActionCreators.updateTradeInOwnershipType(changeOwnershipType, tradeInIndex));
    };

    const getId = (id: string) => `${id}-${tradeInIndex}`;

    const renderLeaseTradeCheckbox = () => {
        return (
            <CheckBox
                checked={tradeInOwnershipType.toLowerCase() === LEASE}
                id={getId(CHECKBOX_IDS.LEASE)}
                label={IS_TRADE_IN_ACTIVE_LEASE}
                onChange={handleOwnershipTypeChange}
                value={tradeInOwnershipType.toLowerCase()}
                disabled={isTradeInDeleted || isEditDisabled || isLocked}
                sx={{ backgroundColor: 'transparent' }}
            />
        );
    };

    const renderBuyerRegisteredCheckbox = () => (
        <CheckBox
            checked={isBuyerRegisteredOwner}
            data-testid={CHECKBOX_IDS.BUYER_REGISTERED_OWNER}
            id={getId(CHECKBOX_IDS.BUYER_REGISTERED_OWNER)}
            label={IS_BUYER_REGISTERED_OWNER}
            onChange={handleBuyerRegisteredOwnerChange}
            value={CHECKBOX_IDS.BUYER_REGISTERED_OWNER}
            disabled={isTradeInDeleted || tradeInputsDisabled || isLocked}
            sx={{ backgroundColor: 'transparent' }}
        />
    );

    const renderVehicleRegisteredCheckbox = () => (
        <CheckBox
            checked={isVehicleRegisteredInSameState}
            label={VEHICLE_IS_REGISTERED_IN_SAME_STATE}
            id={getId(CHECKBOX_IDS.VEHICLE_REGISTERED_SAME_STATE)}
            onChange={handleVehicleRegisteredSameStateChange}
            value={CHECKBOX_IDS.VEHICLE_REGISTERED_SAME_STATE}
            disabled={isTradeInDeleted || tradeInputsDisabled || isLocked}
            sx={{ backgroundColor: 'transparent' }}
        />
    );

    const renderIncludeOfferCheckbox = () => (
        <TradeCheckboxContainer>
            <CheckBox
                checked={shouldIncludeTradeInWithOffer}
                label={INCLUDE_TRADE_WITH_OFFER}
                id={getId(CHECKBOX_IDS.INCLUDE_OFFER)}
                onChange={handleIncludeTradeWithOfferChange}
                value={includeTradeCheckboxValue}
                disabled={isTradeInDeleted || isLocked}
                sx={{ backgroundColor: 'transparent' }}
            />
            <Typography variant="body-xxs" tag="div">
                *Vehicle information is required to include the trade with the offer.
            </Typography>
        </TradeCheckboxContainer>
    );

    switch (type) {
        case CheckboxType.LEASE:
            return renderLeaseTradeCheckbox();

        case CheckboxType.BUYER_REGISTERED:
            return renderBuyerRegisteredCheckbox();

        case CheckboxType.VEHICLE_REGISTERED:
            return renderVehicleRegisteredCheckbox();

        case CheckboxType.INCLUDE_OFFER:
            return renderIncludeOfferCheckbox();

        default:
            return null;
    }
};
